import React from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './ExportButtons.css';

const ExportButtons = ({ 
  data = [], 
  filename = 'export', 
  columns = [], 
  title = 'Data Export',
  showPdf = true,
  showCsv = true,
  showExcel = true,
  showPrint = true,
  className = '',
  size = 'sm', // 'xs', 'sm', 'md', 'lg'
  pdfOrientation = 'portrait', // 'portrait' or 'landscape'
  rowStyler = null, // Function to determine row styling (row) => {fillColor, textColor}
  summaryData = null, // Summary data with totals (totalAmount, totalVatAmount, etc.)
  logo = null, // Logo image for PDF header
  headerInfo = null // Header info with dayName, date, time
}) => {

  // Format data for export (remove unwanted fields, format dates, etc.)
  const formatDataForExport = (rawData) => {
    if (!rawData || rawData.length === 0) return [];
    
    return rawData.map(item => {
      const formattedItem = {};
      
      if (columns.length > 0) {
        // Use specified columns
        columns.forEach(col => {
          if (typeof col === 'string') {
            formattedItem[col] = item[col] || '';
          } else if (typeof col === 'object') {
            const value = item[col.key] || '';
            formattedItem[col.label || col.key] = col.formatter ? col.formatter(value, item) : value;
          }
        });
      } else {
        // Use all columns, but format common fields
        Object.keys(item).forEach(key => {
          let value = item[key];
          
          // Format dates
          if (key.includes('date') || key.includes('created') || key.includes('updated')) {
            if (value) {
              try {
                value = new Date(value).toLocaleDateString();
              } catch (e) {
                // Keep original value if date parsing fails
              }
            }
          }
          
          // Format currency fields
          if (key.includes('balance') || key.includes('amount') || key.includes('price')) {
            if (typeof value === 'number') {
              value = `฿${value.toLocaleString()}`;
            }
          }
          
          formattedItem[key] = value || '';
        });
      }
      
      return formattedItem;
    });
  };

  const formatPdfAmount = (value) => `${(parseFloat(value) || 0).toFixed(2)}`;

  // Export to CSV
  const exportToCSV = () => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const formattedData = formatDataForExport(data);
    const headers = Object.keys(formattedData[0]);
    
    let csvContent = headers.join(',') + '\n';
    
    formattedData.forEach(row => {
      const values = headers.map(header => {
        const value = row[header] || '';
        // Escape commas and quotes in CSV
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvContent += values.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const formattedData = formatDataForExport(data);
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    const formattedData = formatDataForExport(data);
    const doc = new jsPDF(pdfOrientation);
    
    let startY = 8;
    
    // Add logo if provided
    if (logo) {
      try {
        const imgWidth = 35;
        const imgHeight = 15;
        doc.addImage(logo, 'PNG', 14, 5, imgWidth, imgHeight);
        startY = 8;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
        startY = 8;
      }
    }
    
    // Add title and header info in compact format
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(title, logo ? 55 : 14, startY + 4);
    
    // Add date/time information in single horizontal line
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    if (headerInfo) {
      const headerText = `${headerInfo.dayName}  |  ${headerInfo.date}  |  ${headerInfo.time}`;
      doc.text(headerText, logo ? 55 : 14, startY + 10);
      if (headerInfo.fromDate && headerInfo.toDate) {
        const rangeText = `Period: ${headerInfo.fromDate} to ${headerInfo.toDate}`;
        doc.text(rangeText, logo ? 55 : 14, startY + 15);
        if (headerInfo.supplierName) {
          doc.text(`Supplier: ${headerInfo.supplierName}`, logo ? 55 : 14, startY + 20);
          startY = startY + 22;
        } else {
          startY = startY + 17;
        }
      } else {
        if (headerInfo.supplierName) {
          doc.text(`Supplier: ${headerInfo.supplierName}`, logo ? 55 : 14, startY + 15);
          startY = startY + 17;
        } else {
          startY = startY + 12;
        }
      }
    } else {
      const now = new Date();
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = days[now.getDay()];
      const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      const headerText = `${dayName}  |  ${dateStr}  |  ${timeStr}`;
      doc.text(headerText, logo ? 55 : 14, startY + 10);
      startY = startY + 12;
    }
    
    // Prepare table data
    const headers = Object.keys(formattedData[0]);
    const tableData = formattedData.map(row => 
      headers.map(header => String(row[header] || ''))
    );
    
    // Calculate grand total before table
    let grandTotal = 0;
    let amountField = null;
    const amountFields = ['Amount', 'Total Amount', 'amount', 'total_amount', 'Total', 'Grand Total'];

    if (formattedData.length > 0) {
      const firstRow = formattedData[0];

      for (const field of amountFields) {
        if (field in firstRow) {
          amountField = field;
          break;
        }
      }

      if (amountField) {
        formattedData.forEach(row => {
          let value = row[amountField];
          if (typeof value === 'string') {
            value = parseFloat(value.replace(/[฿,]/g, '')) || 0;
          } else {
            value = parseFloat(value) || 0;
          }
          grandTotal += value;
        });
      }
    }

    if (!amountField) {
      amountField = headers.find((header) => /amount|total/i.test(header)) || null;
    }

    const amountColumnIndex = amountField ? headers.indexOf(amountField) : -1;
    const resolvedTotalAmount = summaryData && typeof summaryData.totalAmount !== 'undefined'
      ? parseFloat(summaryData.totalAmount) || 0
      : grandTotal;
    const footerRow = amountColumnIndex >= 0
      ? (() => {
          const labelSpan = Math.max(amountColumnIndex, 1);
          const trailingColumns = Math.max(headers.length - amountColumnIndex - 1, 0);
          const row = [
            {
              content: 'TOTAL AMOUNT',
              colSpan: labelSpan,
              styles: {
                halign: 'right',
                fontSize: 10,
                fontStyle: 'bold'
              }
            },
            {
              content: formatPdfAmount(resolvedTotalAmount),
              styles: {
                halign: 'right',
                fontSize: 10,
                fontStyle: 'bold',
                textColor: [22, 163, 74]
              }
            }
          ];

          if (trailingColumns > 0) {
            row.push({
              content: '',
              colSpan: trailingColumns
            });
          }

          return row;
        })()
      : null;

    // Add table with conditional row styling
    doc.autoTable({
      head: [headers],
      body: tableData,
      foot: footerRow ? [footerRow] : undefined,
      showFoot: footerRow ? 'lastPage' : 'never',
      startY: startY + 5,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 66, 66] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 10 },
      margin: { top: 14, bottom: 20, left: 14, right: 14 },
      didDrawPage: function(data) {
        // Add page numbers in footer
        const pageCount = doc.internal.getNumberOfPages();
        const pageNumber = data.pageNumber;
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100);
        const pageText = `Page ${pageNumber} of ${pageCount}`;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.text(pageText, pageWidth / 2, pageHeight - 10, { align: 'center' });
        
        // Only add header on first page
        if (data.pageNumber === 1) return;
        
        // Add minimal header on subsequent pages (just a line separator)
        doc.setDrawColor(200, 200, 200);
        doc.line(14, 10, doc.internal.pageSize.getWidth() - 14, 10);
        doc.setFontSize(9);
        doc.setTextColor(0);
        doc.text(title, 14, 15);
      },
      didParseCell: function(data) {
        // Apply row styling if rowStyler function is provided
        if (rowStyler && data.section === 'body') {
          const rowIndex = data.row.index;
          const originalRow = formattedData[rowIndex];
          const style = rowStyler(originalRow);
          
          if (style) {
            if (style.fillColor) {
              data.cell.styles.fillColor = style.fillColor;
            }
            if (style.textColor) {
              data.cell.styles.textColor = style.textColor;
            }
          }
        }

      },
      didDrawTable: function(data) {
        if (footerRow) {
          return;
        }

        // Add summary row
        const finalY = data.lastAutoTable.finalY + 8;
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;
        
        // Prepare total display
        const totalLabel = 'GRAND TOTAL:';
        const totalAmount = formatPdfAmount(resolvedTotalAmount);
        
        // Check if there's enough space
        const requiredSpace = 15;
        let totalY = finalY;
        
        if (finalY + requiredSpace > pageHeight - 15) {
          // Not enough space, add new page
          doc.addPage();
          totalY = 20;
          // Add title on new page
          doc.setFontSize(11);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(title, margin, 15);
        }
        
        // Draw separator line
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, totalY - 2, pageWidth - margin, totalY - 2);
        
        // Add total amount text with background
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        
        // Add background color for total row
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, totalY - 1, pageWidth - 2 * margin, 8, 'F');
        
        // Add label and amount
        doc.text(totalLabel, margin + 2, totalY + 4);
        const textWidth = doc.getTextWidth(totalAmount);
        doc.text(totalAmount, pageWidth - margin - textWidth - 2, totalY + 4);
      }
    });

    doc.save(`${filename}.pdf`);
  };

  // Print functionality
  const handlePrint = () => {
    if (!data || data.length === 0) {
      alert('No data to print');
      return;
    }

    const formattedData = formatDataForExport(data);
    const headers = Object.keys(formattedData[0]);
    
    let printContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            .date { text-align: center; margin-bottom: 20px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .zero-stock-row { background-color: #dc3545 !important; color: white !important; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <div class="date">Generated on: ${new Date().toLocaleDateString()}</div>
          <table>
            <thead>
              <tr>${headers.map(header => `<th>${header}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${formattedData.map(row => {
                const style = rowStyler ? rowStyler(row) : null;
                const rowClass = style && style.fillColor ? 'zero-stock-row' : '';
                return `<tr class="${rowClass}">${headers.map(header => `<td>${row[header] || ''}</td>`).join('')}</tr>`;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const buttonSizeClass = {
    xs: 'btn-xs',
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  };

  const iconSize = {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px'
  };

  return (
    <div className={`export-buttons ${className}`}>
      {showPdf && (
        <button
          type="button"
          className={`btn btn-danger ${buttonSizeClass[size]} export-btn`}
          onClick={exportToPDF}
          title="Export to PDF"
        >
          <i className="fa fa-file-pdf-o" style={{ fontSize: iconSize[size] }}></i>
          <span className="btn-text"> PDF</span>
        </button>
      )}
      
      {showExcel && (
        <button
          type="button"
          className={`btn btn-success ${buttonSizeClass[size]} export-btn`}
          onClick={exportToExcel}
          title="Export to Excel"
        >
          <i className="fa fa-file-excel-o" style={{ fontSize: iconSize[size] }}></i>
          <span className="btn-text"> Excel</span>
        </button>
      )}
      
      {showCsv && (
        <button
          type="button"
          className={`btn btn-info ${buttonSizeClass[size]} export-btn`}
          onClick={exportToCSV}
          title="Export to CSV"
        >
          <i className="fa fa-file-text-o" style={{ fontSize: iconSize[size] }}></i>
          <span className="btn-text"> CSV</span>
        </button>
      )}
      
      {showPrint && (
        <button
          type="button"
          className={`btn btn-warning ${buttonSizeClass[size]} export-btn`}
          onClick={handlePrint}
          title="Print"
        >
          <i className="fa fa-print" style={{ fontSize: iconSize[size] }}></i>
          <span className="btn-text"> Print</span>
        </button>
      )}
    </div>
  );
};

export default ExportButtons;
